import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ApplicationState } from 'store';
import { AcademyState } from 'store/modules/academy_store/types';
import { setFloatingState } from 'store/modules/academy_store/actions';
import { setFloatingPref } from 'services/academy_floating';
import Loader from 'components/atoms/loader';
import AcademyPlayer from 'components/atoms/academy_player';
import AcademyButton from 'components/atoms/academy_button';
import { Container } from './styles';

const AcademyLive: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { liveNow, request } = useSelector<ApplicationState, AcademyState>(
    (s) => s.session.academy,
  );

  // Ao entrar na live, volta o estado para "expandido": assim, ao sair da página,
  // o vídeo reaparece como flutuante. O chip/minimizado só persiste enquanto a live não é reaberta.
  useEffect(() => {
    if (liveNow) {
      setFloatingPref(liveNow.id, 'expanded');
      dispatch(setFloatingState('expanded'));
    }
  }, [liveNow?.id, dispatch]);

  // Minimiza a live para o vídeo flutuante e volta para a navegação.
  const minimizeToFloating = () => {
    if (!liveNow) return;
    setFloatingPref(liveNow.id, 'expanded');
    dispatch(setFloatingState('expanded'));
    if (history.length > 1) history.goBack();
    else history.push('/painel');
  };

  if (request.liveNow.loading && !liveNow) {
    return (
      <Container>
        <div className="loading-wrapper">
          <Loader />
        </div>
      </Container>
    );
  }

  if (!liveNow || !liveNow.embed_url) {
    return (
      <Container>
        <div className="empty-state">
          <i className="material-icons">live_tv</i>
          <p>Nenhuma transmissão ao vivo no momento.</p>
          <AcademyButton icon="event" onClick={() => history.push('/academy/calendario')}>
            Ver agenda de transmissões
          </AcademyButton>
        </div>
      </Container>
    );
  }

  const showChat = liveNow.provider === 'youtube' && Boolean(liveNow.video_id);
  const chatSrc = `https://www.youtube.com/live_chat?v=${liveNow.video_id}&embed_domain=${window.location.hostname}`;

  return (
    <Container>
      <div className={`live-stage ${showChat ? 'with-chat' : ''}`}>
        <div className="live-video">
          <AcademyPlayer src={liveNow.embed_url} title={liveNow.title} autoplay />
        </div>
        {showChat && (
          <div className="live-chat">
            <iframe title="Chat da transmissão ao vivo" src={chatSrc} frameBorder="0" />
            <a
              className="chat-comment-link"
              href={`https://www.youtube.com/watch?v=${liveNow.video_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Comentar no YouTube
              <i className="material-icons">open_in_new</i>
            </a>
          </div>
        )}
      </div>

      <div className="live-header-row">
        <div className="live-title">
          <i className="material-icons">radio_button_checked</i>
          Live - {liveNow.title}
        </div>
        <button
          type="button"
          className="minimize-btn"
          onClick={minimizeToFloating}
          title="Assistir enquanto navega pela plataforma"
        >
          <i className="material-icons">fullscreen_exit</i>
          Minimizar
        </button>
      </div>

      {showChat && (
        <a
          className="watch-on-youtube"
          href={`https://www.youtube.com/watch?v=${liveNow.video_id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <i className="material-icons">open_in_new</i>
          Não carregou o vídeo? Assistir no YouTube
        </a>
      )}

      <div className="live-card">
        <div className="badges">
          <span className="badge-pill">Lives</span>
          {liveNow.category && <span className="badge-pill">{liveNow.category}</span>}
        </div>
        {liveNow.speaker && <div className="live-speaker">Com {liveNow.speaker}</div>}
        {liveNow.description && <div className="live-description">{liveNow.description}</div>}
      </div>
    </Container>
  );
};

export default AcademyLive;
